'use client'

import { useSiteConfig } from '@/feature/config/hooks/useSiteConfig.client'
import { ProductBuyBoxAndAvailabilities } from '@/feature/product/components/product-buy-box/ProductBuyBoxAndAvailabilities.client'
import { ProductDescription } from '@/feature/product/components/product-description/ProductDescription.client'
import { ProductDetailsInfosSection } from '@/feature/product/components/product-details-section/ProductDetailsInfosSection.client'
import { ProductDocuments } from '@/feature/product/components/product-documents/ProductDocuments.client'
import {
  hasProductLabels,
  ProductLabels,
} from '@/feature/product/components/product-labels/ProductLabels.client'
import { ProductStoreStockLink } from '@/feature/product/components/product-store-stock-link/ProductStoreStockLink.client'
import { ProductMarketingInsertTag } from '@/feature/product/components/product-tag/ProductMarketingInsertTag.client'
import { ProductTechnicalSpecsSection } from '@/feature/product/components/product-technical-specs/ProductTechnicalSpecsSection.client'
import { useProductInfos } from '@/feature/product/hooks/useProductInfos'
import {
  DESCRIPTION_ANCHOR_ID,
  EXTENDED_CONTENT_REGEX,
  EXTENDED_SANITIZE_OPTIONS,
  PRODUCT_DESCRIPTION_MAX_LENGTH,
  SHORT_DESCRIPTION_SANITIZE_OPTIONS,
} from '@/feature/product/services/product.constants'
import { isStickyVisibleAtom } from '@/feature/product/services/product.store'
import { useIsAuthenticated } from '@/lib/auth/client/AuthProvider.client'
import { useNextIsClientHydrated } from '@/lib/next/useNextIsClientHydrated'
import { getSanitizedPlainText } from '@/lib/utils/text-utils'
import { gql } from '@gql-generated/gql'
import type {
  ProductAvailabilities_PickupPointOfSaleFragment,
  ProductBuyBox_CartFragment,
  ProductBuyBox_FavoritePointOfSaleFragment,
} from '@gql-generated/graphql'
import { ProductType, type ProductDetailInfos_ProductFragment } from '@gql-generated/graphql'
import { useSetAtom } from 'jotai'
import { useEffect, useRef } from 'react'
import { ProductDetailInfosHeader } from './ProductDetailInfosHeader.client'

gql(/* GraphQL */ `
  fragment ProductDetailInfos_Product on Product {
    description
    shortDescription
    ...ProductDetailInfosHeader_Product
    ...ProductMarketingInsertTag_Product
    ...ProductBuyBox_Product
    ...ProductStoreStockLinkProductReference
    documents {
      ...ProductDocuments_Documents
    }
    ...ProductTechnicalSpecsSection_Product
    mainTechnicalSpecs {
      ...ProductTechnicalSpecs_TechnicalSpecs
    }
    norms {
      ...ProductNorms_Norm
    }
    ...ProductLabels_Product
  }
`)

type ProductDetailsInfosProps = {
  /** The correlated product */
  product: ProductDetailInfos_ProductFragment
  favoritePointOfSale?: ProductBuyBox_FavoritePointOfSaleFragment | null
  pickupPointOfSale?: ProductAvailabilities_PickupPointOfSaleFragment | null
  cart?: ProductBuyBox_CartFragment | null
}

export function ProductDetailsInfos({
  product,
  favoritePointOfSale,
  pickupPointOfSale,
  cart,
}: ProductDetailsInfosProps) {
  const setIsStickyVisible = useSetAtom(isStickyVisibleAtom)
  const containerRef = useRef<HTMLDivElement>(null)

  const { activeProduct } = useProductInfos({ product })

  const currentProduct = activeProduct ?? product

  const { config } = useSiteConfig()

  const isAuth = useIsAuthenticated()

  // Defer config-dependent flags to after mount to avoid SSR/client mismatch
  const isMounted = useNextIsClientHydrated()

  const isClickAndCollectEnabled = isMounted
    ? (config?.sales?.isClickAndCollectEnabled ?? false)
    : false
  const isConnectedLocalStockVisible = isMounted
    ? (config?.catalog?.isConnectedLocalStockVisible ?? false)
    : false

  //FIXME: URGENT replace process.env.NEXT_PUBLIC_BRAND !== 'dexis' by some M2 config
  const showStoreStockLink =
    isMounted &&
    process.env.NEXT_PUBLIC_BRAND !== 'dexis' &&
    isAuth &&
    isConnectedLocalStockVisible &&
    currentProduct.type === ProductType.Article

  const shortDescPlainText = getSanitizedPlainText(
    currentProduct.shortDescription,
    SHORT_DESCRIPTION_SANITIZE_OPTIONS,
  )
  // EXTENDED for equality: description may contain tables/iframes absent from shortDesc
  const descPlainTextFull = getSanitizedPlainText(
    currentProduct.description,
    EXTENDED_SANITIZE_OPTIONS,
  )
  // SHORT for length threshold: consistent with what is displayed in the top section
  const descPlainTextShort = getSanitizedPlainText(
    currentProduct.description,
    SHORT_DESCRIPTION_SANITIZE_OPTIONS,
  )
  const hasShortDescription = !!shortDescPlainText && shortDescPlainText !== descPlainTextFull
  const description = hasShortDescription
    ? currentProduct.shortDescription!
    : (currentProduct.description ?? '')

  const hasNormsWithImages = !!currentProduct.norms?.some((norm) => !!norm.image)
  const descPlainTextLength = (hasShortDescription ? shortDescPlainText : descPlainTextShort).length
  const descNeedsTruncation = descPlainTextLength > PRODUCT_DESCRIPTION_MAX_LENGTH
  // img/table/iframe are stripped in SHORT preview but fully rendered in rich section
  const descHasRichContent = EXTENDED_CONTENT_REGEX.test(currentProduct.description ?? '')

  // descHasRichContent (img/table/iframe) always forces anchor regardless of other conditions
  // Cas 1 shortDesc ≤ 300                       => anchor to rich section
  // Cas 1 shortDesc > 300                       => expand/collapse inline
  // Cas 2 + norms images (any length)           => anchor to rich section (always)
  // Cas 2 desc > 300, no norms, no rich content => anchor to rich section
  // Cas 2 desc ≤ 300, no norms, no rich content => shown as-is, no button
  const descriptionAnchor: `#${string}` | undefined =
    descHasRichContent ||
    (hasShortDescription
      ? !descNeedsTruncation
      : hasNormsWithImages || descPlainTextLength > PRODUCT_DESCRIPTION_MAX_LENGTH)
      ? `#${DESCRIPTION_ANCHOR_ID}`
      : undefined

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry) setIsStickyVisible(entry.isIntersecting)
      },
      {
        root: null,
        threshold: 0.25,
      },
    )

    const target = containerRef.current
    if (target) observer.observe(target)

    return () => {
      if (target) observer.unobserve(target)
    }
  }, [setIsStickyVisible])

  return (
    <div ref={containerRef}>
      <ProductDetailInfosHeader product={currentProduct} />

      {currentProduct.marketingInsert && (
        <ProductMarketingInsertTag
          label={currentProduct.marketingInsert.label}
          legalNotice={currentProduct.marketingInsert.legalNotice ?? ''}
        />
      )}

      {/* Force product.type so isMonoDiscriminant stays true between discriminant click and the debounced replaceState (+300ms), preventing filter group flicker */}
      <ProductBuyBoxAndAvailabilities
        product={{ ...currentProduct, type: product.type }}
        cart={cart}
        isClickAndCollectEnabled={isClickAndCollectEnabled}
        favoritePointOfSale={favoritePointOfSale}
        pickupPointOfSale={pickupPointOfSale}
      />

      {showStoreStockLink && (
        <ProductDetailsInfosSection withDivider>
          <ProductStoreStockLink className="self-start" productRef={currentProduct.reference} />
        </ProductDetailsInfosSection>
      )}

      {description && (
        <ProductDetailsInfosSection>
          <ProductDescription
            html={description}
            anchor={descriptionAnchor}
            showTitle={!hasShortDescription && !descriptionAnchor}
          />
        </ProductDetailsInfosSection>
      )}

      {!!currentProduct.mainTechnicalSpecs?.length && (
        <ProductDetailsInfosSection>
          <ProductTechnicalSpecsSection product={currentProduct} />
        </ProductDetailsInfosSection>
      )}

      {!!currentProduct.documents?.length && (
        <ProductDetailsInfosSection data-testid="product-documents">
          <ProductDocuments documents={currentProduct.documents} />
        </ProductDetailsInfosSection>
      )}

      {hasProductLabels(currentProduct) && (
        <ProductDetailsInfosSection data-testid="product-labels">
          <ProductLabels product={currentProduct} />
        </ProductDetailsInfosSection>
      )}
    </div>
  )
}
